import React, { useState } from "react";
import { FaGithub, FaExternalLinkAlt } from "react-icons/fa";

// Project categories
const categories = [
  { id: "all", label: "All Projects" },
  { id: "scraping", label: "Web Scraping" },
  { id: "backend", label: "Backend" },
  { id: "api", label: "API" },
  { id: "automation", label: "Automation" },
  { id: "database", label: "Database" },
];

// Project data
const projects = [
  {
    id: 1,
    title: "eCommerce Price Scraper",
    description:
      "Automated price monitoring scraper built with Puppeteer and Node.js that tracks product prices across multiple eCommerce platforms in Nepal. Sends alerts on price drops and stores historical data in MongoDB for trend analysis.",
    technologies: ["Node.js", "Puppeteer", "MongoDB", "Cron Jobs", "REST API"],
    category: ["scraping", "automation", "backend"],
    links: {
      github: "https://github.com/aashish0241",
    },
  },
  {
    id: 2,
    title: "SERP Rank Tracker",
    description:
      "A SERP scraping tool that monitors keyword rankings on Google search results. Built with Playwright for JavaScript-rendered pages, proxy rotation for anti-bot bypass, and a dashboard API to visualize ranking trends over time.",
    technologies: [
      "Playwright",
      "Node.js",
      "Proxy Rotation",
      "Express",
      "MySQL",
    ],
    category: ["scraping", "automation", "api"],
    links: {
      github: "https://github.com/aashish0241",
    },
  },
  {
    id: 3,
    title: "Business Directory Scraper",
    description:
      "Bulk data extraction tool for scraping business listings, contact details, and addresses from online directories. Features deduplication, CSV/JSON export, and scheduled auto-runs via cron jobs.",
    technologies: ["Cheerio", "Node.js", "MongoDB", "Axios", "Cron Jobs"],
    category: ["scraping", "automation", "database"],
    links: {
      github: "https://github.com/aashish0241",
    },
  },
  {
    id: 4,
    title: "Lead Generation Scraper",
    description:
      "Automated lead scraping pipeline that extracts business emails, phone numbers, and social profiles from targeted websites. Data is cleaned, deduplicated, and delivered via REST API for CRM integration.",
    technologies: ["Node.js", "Puppeteer", "Express", "MongoDB", "REST API"],
    category: ["scraping", "api", "backend"],
    links: {
      github: "https://github.com/aashish0241",
    },
  },
  {
    id: 5,
    title: "School Management System",
    description:
      "A comprehensive Node.js and Express backend for school management with event system and library resource management. Features RESTful API architecture, JWT authentication, and role-based access control.",
    technologies: ["Node.js", "Express", "MongoDB", "JWT", "REST API"],
    category: ["backend", "api"],
    links: {
      live: "http://bindhyeshwori.edu.np/",
      github: "https://github.com/aashish0241",
    },
  },
  {
    id: 6,
    title: "Job Finder Platform",
    description:
      "MERN stack application with real-time job listings, advanced filtering, and user authentication. Backend features include worker profiles, job matching algorithms, and notification systems.",
    technologies: ["MongoDB", "Express", "React", "Node.js", "Socket.io"],
    category: ["backend", "api"],
    links: {
      live: "https://jobfindernepal.netlify.app/find-jobs",
      github: "https://github.com/aashish0241",
    },
  },
  {
    id: 7,
    title: "Scrapy Crawler Pipeline",
    description:
      "A Python Scrapy project that crawls multi-page sites, cleans the extracted items in item pipelines and stores them in PostgreSQL.",
    technologies: ["Python", "Scrapy", "PostgreSQL"],
    category: ["scraping", "database", "automation"],
    links: {
      github: "https://github.com/aashish0241",
    },
  },
  {
    id: 8,
    title: "Node.js Playwright Scraper API",
    description:
      "A Node.js scraper that uses Playwright for JavaScript-rendered pages and serves the collected data through a REST API.",
    technologies: ["Node.js", "Playwright", "Express", "REST API"],
    category: ["scraping", "api", "backend"],
    links: {
      github: "https://github.com/aashish0241",
    },
  },
];

// Project card component
const ProjectCard = ({ project, number, featured }) => (
  <article
    className={`glass-card group flex h-full flex-col p-6 hover:border-primary-500/50 ${
      featured ? "sm:col-span-2 lg:col-span-2 lg:p-8" : ""
    }`}
  >
    <div className="mb-5 flex items-center justify-between gap-4">
      <span className="font-code text-sm text-amber-400">
        {String(number).padStart(2, "0")}
      </span>
      <span className="font-code text-xs text-gray-500">
        {featured
          ? "Featured"
          : categories.find((c) => c.id === project.category[0])?.label}
      </span>
    </div>

    <h3
      className={`font-semibold transition-colors group-hover:text-primary-400 ${
        featured ? "text-2xl" : "text-xl"
      }`}
    >
      {project.title}
    </h3>
    <p className="mt-3 flex-grow text-sm leading-relaxed text-gray-400">
      {project.description}
    </p>

    <ul className="mt-5 flex flex-wrap gap-2">
      {project.technologies.map((tech) => (
        <li
          key={tech}
          className="rounded-md border border-dark-600 bg-dark-700 px-2.5 py-1 font-code text-xs text-gray-300"
        >
          {tech}
        </li>
      ))}
    </ul>

    <div className="mt-6 flex flex-wrap gap-3 border-t border-dark-600 pt-5">
      {project.links.github && (
        <a
          href={project.links.github}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md border border-dark-500 px-3.5 py-2 text-sm text-light-100 transition-colors hover:border-primary-400 hover:text-primary-400"
        >
          <FaGithub aria-hidden="true" />
          Code
        </a>
      )}
      {project.links.live && (
        <a
          href={project.links.live}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-dark-900 transition-colors hover:bg-primary-400"
        >
          <FaExternalLinkAlt aria-hidden="true" className="h-3 w-3" />
          Live demo
        </a>
      )}
    </div>
  </article>
);

// Main Portfolio component
const Portfolio = ({ standalone = false }) => {
  const HeadingTag = standalone ? "h1" : "h2";
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredProjects =
    activeCategory === "all"
      ? projects
      : projects.filter((project) => project.category.includes(activeCategory));

  const countFor = (id) =>
    id === "all"
      ? projects.length
      : projects.filter((project) => project.category.includes(id)).length;

  return (
    <section className="border-t border-dark-600 bg-dark-800/60 py-20 text-light-100 lg:py-24">
      <div className="container mx-auto px-6">
        <header className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <p className="mb-3 font-code text-sm text-primary-400">Work</p>
            <HeadingTag className="mb-4 text-3xl font-bold md:text-5xl md:leading-tight">
              {standalone
                ? "Web Scraping & Backend Development Portfolio in Nepal"
                : "Web Scraping & Backend Projects"}
            </HeadingTag>
            <p className="text-lg text-gray-300">
              Web scraping tools, data extraction pipelines and backend systems
              built for businesses in Nepal and beyond.
            </p>
          </div>
          <a
            href="https://github.com/aashish0241"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-2 self-start rounded-md border border-dark-500 px-4 py-2.5 text-sm transition-colors hover:border-primary-400 hover:text-primary-400 md:self-auto"
          >
            <FaGithub aria-hidden="true" />
            More on GitHub
          </a>
        </header>

        <div
          className="-mx-6 mb-10 flex gap-2 overflow-x-auto px-6 pb-2 md:mx-0 md:flex-wrap md:overflow-visible md:px-0"
          role="group"
          aria-label="Filter projects"
        >
          {categories.map((category) => {
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => setActiveCategory(category.id)}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? "border-primary-500 bg-primary-500 font-semibold text-dark-900"
                    : "border-dark-500 text-gray-300 hover:border-primary-400 hover:text-primary-400"
                }`}
              >
                {category.label}
                <span
                  className={`ml-2 font-code text-xs ${
                    isActive ? "text-dark-900/70" : "text-gray-500"
                  }`}
                >
                  {countFor(category.id)}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              number={project.id}
              featured={activeCategory === "all" && index === 0}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
